import { MbeArrowDropdown, MbeInfo } from '@/components/icons';
import { numberRegex } from '@/constants';
import type { IFragility } from '@/types';
import {
  Flex,
  FormControl,
  FormLabel,
  Heading,
  InputRightElement,
  Select,
  Text,
  Tooltip,
  useControllableState,
  useMediaQuery,
} from '@chakra-ui/react';
import type { ChangeEvent } from 'react';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { categories, ERRORS, fragilityTypes } from '../../constants';
import type { IAuctionHouseInfo } from '../../types';
import { isCategoryWithFragility } from '../../utils';
import { CheckboxCard, MbeInput } from '../shared';

export const ItemInfo: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const [isLargerThan768] = useMediaQuery('(min-width: 48em)');

  const {
    control,
    formState: { errors },
    register,
    watch,
  } = useFormContext<IAuctionHouseInfo>();

  const { field: fragilityField } = useController({
    name: 'item.fragility',
    control,
  });

  const [fragility, setFragility] = useControllableState<IFragility | undefined>({
    value: fragilityField.value,
  });

  const isFragilitySelectorEnabled = isCategoryWithFragility(watch('item.category'));

  return (
    <>
      <Heading fontSize="h4">
        {t('FORM.ITEM_DATA_LABEL')}
        {isLargerThan768 && (
          <Tooltip bgColor="brand.black" label={t('FORM.ITEM_DATA_TOOLTIP')} p={4}>
            <MbeInfo
              _hover={{ color: 'white', bg: 'brand.red.light' }}
              color="brand.red.light"
              ml={2}
              rounded="full"
            />
          </Tooltip>
        )}
      </Heading>
      <MbeInput
        error={errors.item?.name}
        id="itemName"
        placeholder={t('FORM.ITEM_NAME')}
        {...register('item.name')}
      />
      <FormControl variant="floating">
        <Select
          icon={<MbeArrowDropdown color="brand.red.light" />}
          id="itemCategory"
          placeholder={t('FORM.ITEM_CATEGORY')}
          variant="mbe"
          {...register('item.category', {
            onChange(event: ChangeEvent<HTMLSelectElement>) {
              const { value } = event.currentTarget;
              // eslint-disable-next-line unicorn/no-useless-undefined
              if (!isCategoryWithFragility(value)) fragilityField.onChange(undefined);
            },
          })}
        >
          {categories.map((op) => (
            <option key={op.id} value={op.id}>
              {t(op.label)}
            </option>
          ))}
        </Select>
        <FormLabel htmlFor="itemCategory">{t('FORM.ITEM_CATEGORY')}</FormLabel>
      </FormControl>
      {isFragilitySelectorEnabled && (
        <>
          <Heading fontSize="h4">
            {t('FORM.ITEM_FRAGILITY_LABEL')}
            {isLargerThan768 && (
              <Tooltip bgColor="brand.black" label={t('FORM.ITEM_FRAGILITY_TOOLTIP')} p={4}>
                <MbeInfo
                  _hover={{ color: 'white', bg: 'brand.red.light' }}
                  color="brand.red.light"
                  ml={2}
                  rounded="full"
                />
              </Tooltip>
            )}
          </Heading>
          <Flex flexWrap="wrap" gap={4} justifyContent="space-between">
            {fragilityTypes.map((op) => {
              return (
                <CheckboxCard
                  checked={fragility === op.id}
                  key={op.id}
                  name={fragilityField.name}
                  ref={fragilityField.ref}
                  value={op.id}
                  onChange={(ev: ChangeEvent<HTMLInputElement>): void => {
                    const { checked: isChecked, value } = ev.target;
                    const fragValue = Number.parseInt(value, 10) as IFragility;
                    setFragility(isChecked ? undefined : fragValue);
                    fragilityField.onChange(!isChecked ? undefined : fragValue);
                  }}
                >
                  <Text>{t(op.label)}</Text>
                </CheckboxCard>
              );
            })}
          </Flex>
        </>
      )}
      <Heading fontSize="h4">{t('FORM.ITEM_DIMENSIONS_LABEL')}</Heading>
      <MbeInput
        error={errors.item?.weight}
        id="itemWeight"
        placeholder={t('FORM.WEIGHT')}
        {...register('item.weight', {
          pattern: { value: numberRegex, message: t(ERRORS.invalidField) },
        })}
        rightElement={<InputRightElement>Kg</InputRightElement>}
      />
      <MbeInput
        error={errors.item?.length}
        id="itemLength"
        placeholder={t('FORM.LENGTH')}
        {...register('item.length', {
          pattern: { value: numberRegex, message: t(ERRORS.invalidField) },
        })}
        rightElement={<InputRightElement>cm</InputRightElement>}
      />
      <MbeInput
        error={errors.item?.height}
        id="itemtHeight"
        placeholder={t('FORM.HEIGHT')}
        {...register('item.height', {
          pattern: { value: numberRegex, message: t(ERRORS.invalidField) },
        })}
        rightElement={<InputRightElement>cm</InputRightElement>}
      />
      <MbeInput
        error={errors.item?.width}
        id="itemtWidth"
        placeholder={t('FORM.WIDTH')}
        {...register('item.width', {
          pattern: { value: numberRegex, message: t(ERRORS.invalidField) },
        })}
        rightElement={<InputRightElement>cm</InputRightElement>}
      />
    </>
  );
};
